import { Component, Input, Output, EventEmitter, ViewChild, ElementRef, OnChanges, SimpleChange, ChangeDetectionStrategy } from "@angular/core";

import { BaseComponent, UserService, environment, WindowService, EventBusService, WorkspaceService, ContentService, HelperService, CommunityService } from "@mypxplat/xplat/core";
import { VideoNotesBaseComponent } from "@mypxplat/xplat/features";
import { AppService, WebCommunityService } from "@mypxplat/xplat/web/core";
import { TranslateService } from "@ngx-translate/core";
import { take } from "rxjs/operators";
import { MessageInputComponent } from "../message-input/message-input.component";
import { EditCommentComponent, SimpleSignupComponent } from "../modals";

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "myp-video-notes",
  templateUrl: "video-notes.component.html",
  standalone: false,
})
export class VideoNotesComponent extends VideoNotesBaseComponent {
  @Input() height: number;
  @Input() fullHeight: boolean;
  @Input() forceDark: boolean;
  public isHovering = false;

  /**
   * True while notes are being fetched for the first time. Callers to this component don't
   * guarantee the ContentService cache is populated (e.g. the user opened the player directly
   * from a livestream link without visiting a Learn page first), so we fetch on demand and show
   * a spinner instead of the "no notes yet" empty state.
   */
  public loading: boolean = false;

  private _activeTime: any;
  @Input() set activeTime(time) {
    this._activeTime = time;
    if (this.timedVideoNotes && this.timedVideoNotes.length) {
      this.timedVideoNotes.forEach((item) => {
        if (parseInt(item.video_timestamp) - 5 < time.seconds && parseInt(item.video_timestamp) + 5 > time.seconds) {
          item.active = true;
        } else {
          if (item.active) item.active = false;
        }
      });
    }
  }

  get activeTime() {
    return this._activeTime;
  }

  @Output() playVideoFrom: EventEmitter<any> = new EventEmitter();

  public noteInputHeight: number = 44;

  @ViewChild("msgInput") msgInput: MessageInputComponent;
  @ViewChild("chatWindow") chatWindow: ElementRef;

  constructor(
    public userService: UserService,
    private _win: WindowService,
    private _eventbusService: EventBusService,
    public workspaceService: WorkspaceService,
    public appService: AppService,
    public helperService: HelperService,
    public contentService: ContentService,
    public communityService: WebCommunityService,
    private translate: TranslateService
  ) {
    super(userService, contentService, communityService);
  }

  ngOnInit(): void {
    // Base init() reads from the ContentService cache. If it's empty for this video, fetch and
    // then re-init so the notes actually appear. Guard on already having a keyed entry (even an
    // empty array) so we don't re-fetch after a delete on a video that legitimately has none.
    super.ngOnInit();
    if (!this.contentService.myVideoNotesByVideoId || !this.contentService.myVideoNotesByVideoId[this.videoId]) {
      this.loading = true;
      this.contentService.getVideoNotes().subscribe({
        next: () => {
          this.loading = false;
          this.init();
        },
        error: () => {
          this.loading = false;
        },
      });
    }
  }

  noteValueChange(args) {
    this.note = args;
  }

  addNote() {
    if (!this.note) return;
    // Optimistically render the note immediately with a "sending..." label. The real note replaces
    // it via init() once the API call resolves; on error we flip the pending item to a failed
    // state so the user can see it didn't go through and retry.
    const activeTime = this.activeTime;
    const linkToTimestamp = this.linkToTimestamp;
    const text = this.note;
    const pending: any = {
      id: "pending-" + Date.now() + "-" + Math.random().toString(36).slice(2, 8),
      pending: true,
      text,
      message: text,
      created: Date.now(),
      user_id: this.userService.user?.id,
    };
    if (linkToTimestamp) pending.video_timestamp = activeTime?.seconds || 0;
    this.communityService.buildHtmlComment(pending);
    // Insert into the same array (and matching sort order) that init() uses so the pending note
    // shows up at its final position immediately. Otherwise it lands at the end and then jumps
    // when init() runs on API success — jarring for the user.
    if (pending.video_timestamp) {
      const next = [...(this.timedVideoNotes || []), pending];
      next.sort((a, b) => (parseInt(a.video_timestamp) > parseInt(b.video_timestamp) ? 1 : -1));
      this.timedVideoNotes = next;
    } else {
      const next = [...(this.videoNotes || []), pending];
      next.sort((a, b) => (parseInt(a.created) > parseInt(b.created) ? 1 : -1));
      this.videoNotes = next;
    }
    this.scrollChatToBottom();

    const args: any = { text, video_id: this.videoId };
    if (linkToTimestamp) args.video_timestamp = activeTime?.seconds || 0;
    this.contentService.createVideoNote(args).subscribe({
      next: (result) => {
        if (!this.contentService.myVideoNotesByVideoId[this.videoId]) this.contentService.myVideoNotesByVideoId[this.videoId] = [];
        this.contentService.myVideoNotesByVideoId[this.videoId].push(result.video_note);
        this.contentService.myVideoNotesMap[result.video_note.id] = result.video_note;
        // init() rebuilds videoNotes/timedVideoNotes from the service cache, which drops our
        // pending item (it was only in the local arrays) and inserts the real one.
        this.init();
        this.scrollChatToBottom();
      },
      error: () => {
        pending.pending = false;
        pending.sendFailed = true;
      },
    });

    this.note = "";
    this.msgInput.emptyInput();
  }

  scrollChatToBottom() {
    this._win.setTimeout(() => {
      if (this.chatWindow && this.chatWindow.nativeElement) {
        this.chatWindow.nativeElement.scrollTop = this.chatWindow.nativeElement.scrollHeight;
      }
    }, 25);
  }

  inputHeightChange(args) {
    this.noteInputHeight = args;
  }

  editNote(note) {
    const modalRef = this.appService.showModal(EditCommentComponent, { size: "lg" }, true, true);
    modalRef.componentInstance.comment = note;
    modalRef.componentInstance.isVideoNote = true;
    modalRef.result.then(
      (result) => {
        if (result) {
          this.contentService.updateVideoNote({ id: result.id, text: result.text }).subscribe((result) => {
            this.timedVideoNotes.forEach((item) => {
              if (item.id == result.video_note.id) {
                item.text = result.video_note.text;
                this.communityService.buildHtmlComment(item);
              }
            });
            this.videoNotes.forEach((item) => {
              if (item.id == result.video_note.id) {
                item.text = result.video_note.text;
                this.communityService.buildHtmlComment(item);
              }
            });
          });
          // this.db.doc("video_comments/" + msg.videoID + "/comments/" + msg.key).update({ message: result.message, edited: true });
        }
      },
      (error) => {}
    );
  }

  noteOptions(note) {
    this.translate.get(["video_notes.edit_note_action", "video_notes.delete_note_action", "video_notes.note_options_title"]).subscribe((translations: any) => {
      const editNoteText = translations["video_notes.edit_note_action"];
      const deleteNoteText = translations["video_notes.delete_note_action"];
      let args: any = {
        title: translations["video_notes.note_options_title"],
        actions: [editNoteText, deleteNoteText],
      };
      this._eventbusService.emit(this._eventbusService.types.showActionChooser, args);
      this._eventbusService
        .observe(this._eventbusService.types.actionChosen)
        .pipe(take(1))
        .subscribe((result) => {
          if (result) {
            if (result == editNoteText) {
              this.editNote(note);
            } else if (result == deleteNoteText) {
              this.deleteNote(note);
            }
          }
        });
    });
  }
}
